import React from 'react';
import { AlertCircle, XCircle, CheckCircle, Info, Trash2 } from 'lucide-react';

/**
 * Reusable confirmation dialog — replaces native window.confirm() with the
 * app's gradient modal style (same look as ToastModal / trip-confirm modals).
 *
 * Props:
 *   isOpen       - whether the modal is visible
 *   onClose      - called when the user cancels or dismisses
 *   onConfirm    - called when the user confirms
 *   title        - heading text
 *   message      - body text
 *   type         - 'danger' | 'warning' | 'success' | 'info' (controls color/icon)
 *   confirmText  - confirm button label (default "Confirm")
 *   cancelText   - cancel button label (default "Cancel")
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  message,
  type = 'danger',
  confirmText = 'Confirm',
  cancelText = 'Cancel'
}) => {
  if (!isOpen) return null;

  const configs = {
    danger: {
      bg: 'bg-gradient-to-r from-red-500 to-rose-600',
      btnBg: 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700',
      icon: Trash2
    },
    warning: {
      bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
      btnBg: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700',
      icon: AlertCircle
    },
    success: {
      bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
      btnBg: 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700',
      icon: CheckCircle
    },
    info: {
      bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
      btnBg: 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700',
      icon: Info
    }
  };

  const config = configs[type] || configs.danger;
  const Icon = config.icon;

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl animate-bounce-in">
        {/* Header */}
        <div className={`${config.bg} px-6 py-6 text-center`}>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>

        {/* Body */}
        <div className="p-6 text-center">
          {message && <p className="text-gray-600 mb-6">{message}</p>}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold transition-all"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              className={`flex-1 ${config.btnBg} text-white py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
